import { useState, useEffect } from "react";
import { MapPin, AlertTriangle, TrendingDown, TrendingUp, RefreshCw, Shield, Target, ArrowDown, ArrowUp, Info, ChevronDown, ChevronUp } from "lucide-react";
import getApiUrl from '../utils/getApiUrl';

const API = getApiUrl() + "/api";

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};

const DEPT_NAMES = {
  "01":"Ain","02":"Aisne","03":"Allier","04":"Alpes-de-Haute-Provence","05":"Hautes-Alpes",
  "06":"Alpes-Maritimes","07":"Ardèche","08":"Ardennes","09":"Ariège","10":"Aube",
  "11":"Aude","12":"Aveyron","13":"Bouches-du-Rhône","14":"Calvados","15":"Cantal",
  "16":"Charente","17":"Charente-Maritime","18":"Cher","19":"Corrèze","21":"Côte-d'Or",
  "22":"Côtes-d'Armor","23":"Creuse","24":"Dordogne","25":"Doubs","26":"Drôme",
  "27":"Eure","28":"Eure-et-Loir","29":"Finistère","30":"Gard","31":"Haute-Garonne",
  "32":"Gers","33":"Gironde","34":"Hérault","35":"Ille-et-Vilaine","36":"Indre",
  "37":"Indre-et-Loire","38":"Isère","39":"Jura","40":"Landes","41":"Loir-et-Cher",
  "42":"Loire","43":"Haute-Loire","44":"Loire-Atlantique","45":"Loiret","46":"Lot",
  "47":"Lot-et-Garonne","48":"Lozère","49":"Maine-et-Loire","50":"Manche","51":"Marne",
  "52":"Haute-Marne","53":"Mayenne","54":"Meurthe-et-Moselle","55":"Meuse","56":"Morbihan",
  "57":"Moselle","58":"Nièvre","59":"Nord","60":"Oise","61":"Orne","62":"Pas-de-Calais",
  "63":"Puy-de-Dôme","64":"Pyrénées-Atlantiques","65":"Hautes-Pyrénées","66":"Pyrénées-Orientales",
  "67":"Bas-Rhin","68":"Haut-Rhin","69":"Rhône","70":"Haute-Saône","71":"Saône-et-Loire",
  "72":"Sarthe","73":"Savoie","74":"Haute-Savoie","75":"Paris","76":"Seine-Maritime",
  "77":"Seine-et-Marne","78":"Yvelines","79":"Deux-Sèvres","80":"Somme","81":"Tarn",
  "82":"Tarn-et-Garonne","83":"Var","84":"Vaucluse","85":"Vendée","86":"Vienne",
  "87":"Haute-Vienne","88":"Vosges","89":"Yonne","90":"Territoire de Belfort",
  "91":"Essonne","92":"Hauts-de-Seine","93":"Seine-Saint-Denis","94":"Val-de-Marne","95":"Val-d'Oise"
};

export default function GeoLeaksPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAllDepts, setShowAllDepts] = useState(false);
  const [showAllSources, setShowAllSources] = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await authFetch(API + "/google-ads/geo-leaks-analysis");
      if (!res.ok) throw new Error("Erreur chargement");
      const json = await res.json();
      setData(json);
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw className="w-8 h-8 text-red-400 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-6 text-center">
        <AlertTriangle className="w-8 h-8 text-red-400 mx-auto mb-3" />
        <p className="text-red-300">{error}</p>
        <button onClick={loadData} className="mt-3 px-4 py-2 bg-red-500/20 hover:bg-red-500/30 rounded-lg text-sm text-red-300">
          Réessayer
        </button>
      </div>
    );
  }

  if (!data) return null;

  const maxHz = data.hors_zone_by_dept?.length > 0 ? data.hors_zone_by_dept[0].count : 1;
  const trend = data.monthly_trend || [];
  const maxMonthTotal = Math.max(...trend.map(m => m.total), 1);
  const visibleDepts = showAllDepts ? data.hors_zone_by_dept : (data.hors_zone_by_dept || []).slice(0, 10);
  const visibleSources = showAllSources ? data.by_source : (data.by_source || []).slice(0, 5);

  return (
    <div className="space-y-6" data-testid="geo-leaks-panel">
      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-red-500/15 to-red-600/5 border border-red-500/25 rounded-xl p-5" data-testid="kpi-hz-total">
          <div className="flex items-center justify-between mb-2">
            <AlertTriangle className="w-5 h-5 text-red-400" />
            <span className="text-xs text-red-300/70 font-medium">GLOBAL</span>
          </div>
          <p className="text-3xl font-bold text-red-300">{data.hors_zone_pct}%</p>
          <p className="text-sm text-slate-400 mt-1">Leads hors zone</p>
          <p className="text-xs text-slate-500">{data.hors_zone_leads} sur {data.total_leads}</p>
        </div>

        <div className="bg-gradient-to-br from-amber-500/15 to-amber-600/5 border border-amber-500/25 rounded-xl p-5" data-testid="kpi-hz-current">
          <div className="flex items-center justify-between mb-2">
            <MapPin className="w-5 h-5 text-amber-400" />
            <span className="text-xs text-amber-300/70 font-medium">CE MOIS</span>
          </div>
          <p className="text-3xl font-bold text-amber-300">{data.current_month?.hz_pct || 0}%</p>
          <p className="text-sm text-slate-400 mt-1">{data.current_month?.hz || 0} HZ / {data.current_month?.total || 0}</p>
        </div>

        <div className="bg-gradient-to-br from-slate-500/15 to-slate-600/5 border border-slate-500/25 rounded-xl p-5" data-testid="kpi-hz-prev">
          <div className="flex items-center justify-between mb-2">
            <Target className="w-5 h-5 text-slate-400" />
            <span className="text-xs text-slate-400/70 font-medium">MOIS PREC.</span>
          </div>
          <p className="text-3xl font-bold">{data.prev_month?.hz_pct || 0}%</p>
          <p className="text-sm text-slate-400 mt-1">{data.prev_month?.hz || 0} HZ / {data.prev_month?.total || 0}</p>
        </div>

        <div className={`bg-gradient-to-br rounded-xl p-5 border ${data.trend_delta <= 0 ? "from-green-500/15 to-green-600/5 border-green-500/25" : "from-red-500/15 to-red-600/5 border-red-500/25"}`} data-testid="kpi-hz-trend">
          <div className="flex items-center justify-between mb-2">
            {data.trend_delta <= 0 ? <TrendingDown className="w-5 h-5 text-green-400" /> : <TrendingUp className="w-5 h-5 text-red-400" />}
            <span className="text-xs font-medium" style={{color: data.trend_delta <= 0 ? "rgb(134,239,172)" : "rgb(252,165,165)"}}>TENDANCE</span>
          </div>
          <p className={`text-3xl font-bold ${data.trend_delta <= 0 ? "text-green-300" : "text-red-300"}`}>
            {data.trend_delta > 0 ? "+" : ""}{data.trend_delta}pts
          </p>
          <p className="text-sm text-slate-400 mt-1">vs mois précédent</p>
        </div>
      </div>

      {/* Self-declared insight */}
      {data.self_declared && data.self_declared.hors_idf > 0 && (
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 flex items-start gap-3" data-testid="self-declared-insight">
          <Info className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-amber-200 font-medium">Insight formulaire landing page</p>
            <p className="text-sm text-slate-400 mt-1">
              <span className="text-amber-300 font-semibold">{data.self_declared.hors_idf}</span> leads HZ se sont eux-memes declares "Hors Ile-de-France" dans le formulaire
              {data.self_declared.idf_but_hz > 0 && <>, <span className="text-slate-300">{data.self_declared.idf_but_hz}</span> ont declare "IDF" mais ont un dept hors zone</>}.
              Le formulaire cree le lead malgre tout.
            </p>
          </div>
        </div>
      )}

      {/* Monthly Trend Chart */}
      <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
        <h3 className="font-semibold mb-5 flex items-center gap-2 text-base">
          <TrendingDown className="w-5 h-5 text-red-400" />
          Tendance mensuelle hors zone
        </h3>
        <div className="flex items-end gap-1.5 h-48">
          {trend.map((m, i) => {
            const hzH = m.total > 0 ? (m.hz / maxMonthTotal) * 100 : 0;
            const izH = m.total > 0 ? (m.iz / maxMonthTotal) * 100 : 0;
            return (
              <div key={m.month} className="flex-1 flex flex-col items-center gap-1 group relative" data-testid={`trend-bar-${m.month}`}>
                <div className="w-full flex flex-col justify-end" style={{height: "160px"}}>
                  <div className="w-full bg-green-500/30 rounded-t transition-all group-hover:bg-green-500/50" style={{height: `${izH}%`}} />
                  <div className="w-full bg-red-500/40 rounded-b transition-all group-hover:bg-red-500/60" style={{height: `${hzH}%`}} />
                </div>
                <span className="text-[10px] text-slate-500 truncate w-full text-center">{m.month.slice(5)}/{m.month.slice(2,4)}</span>
                {/* Tooltip */}
                <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 bg-slate-900 border border-slate-700 rounded-lg p-2 text-xs hidden group-hover:block z-10 whitespace-nowrap">
                  <p className="text-slate-300 font-medium">{m.month}</p>
                  <p className="text-green-400">En zone: {m.iz}</p>
                  <p className="text-red-400">Hors zone: {m.hz} ({m.hz_pct}%)</p>
                </div>
              </div>
            );
          })}
        </div>
        <div className="flex items-center gap-6 mt-3 justify-center">
          <div className="flex items-center gap-2 text-xs text-slate-400"><div className="w-3 h-3 rounded bg-green-500/40" /> En zone</div>
          <div className="flex items-center gap-2 text-xs text-slate-400"><div className="w-3 h-3 rounded bg-red-500/40" /> Hors zone</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top departments */}
        <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
          <h3 className="font-semibold mb-4 flex items-center gap-2 text-base">
            <MapPin className="w-5 h-5 text-red-400" />
            Top departements hors zone
          </h3>
          <div className="space-y-2">
            {visibleDepts.map((d) => {
              const pct = Math.round((d.count / maxHz) * 100);
              const name = DEPT_NAMES[d.dept] || DEPT_NAMES[d.dept.padStart(2, '0')] || d.dept;
              return (
                <div key={d.dept} className="flex items-center gap-3" data-testid={`dept-${d.dept}`}>
                  <span className="w-8 text-sm text-slate-400 text-right font-mono">{d.dept}</span>
                  <span className="w-36 text-sm text-slate-300 truncate">{name}</span>
                  <div className="flex-1 h-5 bg-slate-700/40 rounded overflow-hidden">
                    <div className="h-full bg-red-500/40 rounded transition-all" style={{width: `${pct}%`}} />
                  </div>
                  <span className="w-12 text-sm text-right font-medium">{d.count}</span>
                </div>
              );
            })}
          </div>
          {(data.hors_zone_by_dept || []).length > 10 && (
            <button onClick={() => setShowAllDepts(!showAllDepts)} className="mt-3 flex items-center gap-1 text-xs text-slate-400 hover:text-slate-300 mx-auto">
              {showAllDepts ? <><ChevronUp className="w-3 h-3" />Voir moins</> : <><ChevronDown className="w-3 h-3" />Voir les {data.hors_zone_by_dept.length} departements</>}
            </button>
          )}
        </div>

        {/* By source */}
        <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
          <h3 className="font-semibold mb-4 flex items-center gap-2 text-base">
            <Target className="w-5 h-5 text-amber-400" />
            Fuites par source
          </h3>
          <div className="space-y-3">
            {visibleSources.map((s) => {
              const total = s.total || 1;
              const hzPct = Math.round((s.hors_zone / total) * 100);
              const izPct = 100 - hzPct;
              return (
                <div key={s.source} data-testid={`source-${s.source}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-slate-300 truncate max-w-[200px]">{s.source || "INCONNU"}</span>
                    <div className="flex items-center gap-3 text-xs">
                      <span className="text-slate-400">{s.total} leads</span>
                      <span className={s.hors_zone_pct > 30 ? "text-red-400 font-semibold" : "text-amber-400"}>
                        {s.hors_zone_pct}% HZ
                      </span>
                    </div>
                  </div>
                  <div className="h-4 bg-slate-700/40 rounded overflow-hidden flex">
                    <div className="h-full bg-green-500/40" style={{width: `${izPct}%`}} />
                    <div className="h-full bg-red-500/50" style={{width: `${hzPct}%`}} />
                  </div>
                </div>
              );
            })}
          </div>
          {(data.by_source || []).length > 5 && (
            <button onClick={() => setShowAllSources(!showAllSources)} className="mt-3 flex items-center gap-1 text-xs text-slate-400 hover:text-slate-300 mx-auto">
              {showAllSources ? <><ChevronUp className="w-3 h-3" />Voir moins</> : <><ChevronDown className="w-3 h-3" />Toutes les sources</>}
            </button>
          )}
        </div>
      </div>

      {/* Recent HZ leads table */}
      {data.recent_hors_zone && data.recent_hors_zone.length > 0 && (
        <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-700/30">
            <h3 className="font-semibold flex items-center gap-2 text-base">
              <AlertTriangle className="w-5 h-5 text-red-400" />
              Derniers leads hors zone
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="recent-hz-table">
              <thead>
                <tr className="text-left text-xs text-slate-500 border-b border-slate-700/30">
                  <th className="px-4 py-3">Nom</th>
                  <th className="px-4 py-3">Dept</th>
                  <th className="px-4 py-3">CP</th>
                  <th className="px-4 py-3">Source</th>
                  <th className="px-4 py-3">Date</th>
                </tr>
              </thead>
              <tbody>
                {[...data.recent_hors_zone].reverse().map((l, i) => (
                  <tr key={i} className="border-b border-slate-700/20 hover:bg-slate-700/20">
                    <td className="px-4 py-2.5 text-slate-300">{l.nom} {l.prenom}</td>
                    <td className="px-4 py-2.5">
                      <span className="px-2 py-0.5 rounded bg-red-500/20 text-red-300 text-xs font-mono">
                        {l.dpt} - {DEPT_NAMES[l.dpt] || DEPT_NAMES[l.dpt?.padStart(2, '0')] || "?"}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-slate-400 font-mono text-xs">{l.cp}</td>
                    <td className="px-4 py-2.5 text-slate-400 text-xs truncate max-w-[180px]">{l.source}</td>
                    <td className="px-4 py-2.5 text-slate-500 text-xs">{l.date?.slice(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Recommendations */}
      <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
        <h3 className="font-semibold mb-4 flex items-center gap-2 text-base">
          <Shield className="w-5 h-5 text-green-400" />
          Preconisations pour reduire les fuites
        </h3>
        <div className="space-y-3">
          <RecoCard
            priority="CRITIQUE"
            title="Bloquer les leads HZ au niveau du formulaire Qhare"
            description="Le formulaire capte deja la region (IDF / Hors IDF) mais cree le lead dans tous les cas. Configurer Qhare pour ne PAS creer de lead quand 'Hors IDF' est selectionne."
            impact={`Eliminerait ~${data.self_declared?.hors_idf || 0} leads sur ${data.hors_zone_leads} HZ (${data.self_declared?.hors_idf && data.hors_zone_leads ? Math.round(data.self_declared.hors_idf / data.hors_zone_leads * 100) : 0}%)`}
            color="red"
          />
          <RecoCard
            priority="HAUTE"
            title="Ajouter un filtre departement sur la landing page"
            description="Demander le code postal en debut de formulaire. Si le CP est hors zone, afficher 'Desole, nous n'intervenons pas dans votre zone' et bloquer la soumission."
            impact="Eliminerait 100% des leads HZ a la source"
            color="amber"
          />
          <RecoCard
            priority="MOYENNE"
            title="Verifier le SEO de la landing page"
            description="Si prime-adapt-seniors.fr est indexee sur Google, elle recoit du trafic organique de toute la France, independamment du ciblage Google Ads."
            impact="Reduirait le trafic organique hors zone"
            color="blue"
          />
          <RecoCard
            priority="INFO"
            title="Le ciblage Google Ads est correct"
            description={`Les 2 campagnes ciblent IDF + Oise + Aisne en mode PRESENCE. 100% des depenses sont en zone. Les exclusions couvrent ${data.zones_actives?.length || 0} departements actifs.`}
            impact="Aucune action requise cote Google Ads"
            color="green"
          />
        </div>
      </div>
    </div>
  );
}

function RecoCard({ priority, title, description, impact, color }) {
  const colors = {
    red: "border-red-500/30 bg-red-500/5",
    amber: "border-amber-500/30 bg-amber-500/5",
    blue: "border-blue-500/30 bg-blue-500/5",
    green: "border-green-500/30 bg-green-500/5",
  };
  const badgeColors = {
    red: "bg-red-500/20 text-red-300",
    amber: "bg-amber-500/20 text-amber-300",
    blue: "bg-blue-500/20 text-blue-300",
    green: "bg-green-500/20 text-green-300",
  };
  return (
    <div className={`border rounded-lg p-4 ${colors[color]}`}>
      <div className="flex items-start gap-3">
        <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${badgeColors[color]}`}>{priority}</span>
        <div className="flex-1">
          <p className="text-sm font-medium text-slate-200">{title}</p>
          <p className="text-xs text-slate-400 mt-1">{description}</p>
          <p className="text-xs text-slate-500 mt-2 italic">Impact: {impact}</p>
        </div>
      </div>
    </div>
  );
}
